import { useCallback } from "react";
import {
  useNavigationHistoryActions,
  useNavigationHistoryState,
} from "~app/contexts/navigation-history-context";

/**
 * Return type for useNavigationHistory hook
 */
export interface UseNavigationHistoryReturn {
  updateCurrentPath: (pathname: string, search: string) => void;
  onLocationChange: (newPath: string) => void;
}

/**
 * Custom hook that provides navigation history functionality
 * with helper methods that use the standard context actions
 */
export function useNavigationHistory(): UseNavigationHistoryReturn {
  const { setPreviousPath, setCurrentPath } = useNavigationHistoryActions();
  const { currentPath } = useNavigationHistoryState();

  /**
   * Updates the current path with pathname and query params
   * Used when query params change via window.history.replaceState
   */
  const updateCurrentPath = useCallback(
    (pathname: string, search: string) => {
      const fullPath = search ? `${pathname}?${search}` : pathname;
      setCurrentPath(fullPath);
    },
    [setCurrentPath]
  );

  /**
   * Handles location changes from Remix navigation
   * Saves the current path as previous and sets the new path as current
   * If the pathname hasn't changed (only query params might have), preserve the currentPath's query params
   */
  const onLocationChange = useCallback(
    (newPath: string) => {
      // Extract pathname from newPath (remove query params if any)
      const newPathname = newPath.split("?")[0];
      if (currentPath) {
        const currentPathname = currentPath.split("?")[0];
        if (currentPathname === newPathname) {
          return;
        }
        setPreviousPath(currentPath);
      }
      setCurrentPath(newPath);
    },
    [currentPath, setPreviousPath, setCurrentPath]
  );

  return {
    updateCurrentPath,
    onLocationChange,
  };
}
